import { describeEdgeFunctionError } from "./edgeFunctionError";

/**
 * Make every `supabase.functions.invoke` failure carry a specific message.
 *
 * WHY A PROTOTYPE PATCH. There are ~180 invoke call sites, and most of them do
 * `if (error) throw error` and toast `error.message` — which supabase-js fixes to
 * "Edge Function returned a non-2xx status code" whatever the function said. Fixing
 * that one call site at a time leaves the rest showing the generic text (staff saw
 * it on a 2 MB signature upload that the server had refused with a precise reason).
 *
 * `supabase.functions` is a GETTER that builds a new FunctionsClient on every
 * access, so the instance cannot be patched; its prototype can. One shared copy of
 * @supabase/functions-js is installed, so this covers every call.
 *
 * WHAT IT CHANGES: only `error.message`. The error object, its class, and
 * `error.context` (the Response) are untouched, and the body is read through
 * `clone()`, so the ~70 call sites that read the body themselves keep working.
 * `data` and the success path are never touched.
 */
type InvokeResult = { data: unknown; error: unknown };
type Invoke = (functionName: string, options?: unknown) => Promise<InvokeResult>;

const INSTALLED = Symbol.for("qf.edgeFunctionErrorMessages");

export function installEdgeFunctionErrorMessages(client: { functions: object }): void {
  const proto = Object.getPrototypeOf(client.functions) as Record<string | symbol, unknown>;
  if (proto[INSTALLED]) return; // idempotent — HMR and tests may call it twice
  const original = proto.invoke as Invoke | undefined;
  if (typeof original !== "function") return;

  const wrapped: Invoke = async function (this: unknown, functionName, options) {
    const result = await original.call(this, functionName, options);
    if (result?.error instanceof Error) {
      try {
        result.error.message = await describeEdgeFunctionError(result.error, functionName);
      } catch {
        // Describing must never turn a reported failure into a thrown one.
      }
    }
    return result;
  };

  proto.invoke = wrapped;
  proto[INSTALLED] = true;
}
